import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  MapPin, Calendar, Clock, Users, Star, ShieldCheck, Anchor, Mail, Fish,
  Heart, Share2, Check, ArrowLeft, MessageCircle, Facebook, Info, HandCoins
} from "lucide-react";
import { api, formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import Avatar from "../components/Avatar";
import FounderBadge from "../components/FounderBadge";

function fmtDateTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })
      + " · " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function TripDetail() {
  const { tripId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [seats, setSeats] = useState(1);
  const [message, setMessage] = useState("");
  const [booked, setBooked] = useState(false);
  const [favorited, setFavorited] = useState(false);

  const { data: trip, isLoading, refetch } = useQuery({
    queryKey: ["trip", tripId],
    queryFn: async () => (await api.get(`/trips/${tripId}`)).data,
  });

  useEffect(() => {
    if (trip) document.title = `${trip.title} — Le Bon Matelot`;
  }, [trip]);

  const handleBook = async () => {
    if (!user) { nav("/connexion?next=/sortie/" + tripId); return; }
    try {
      await api.post("/bookings", { trip_id: tripId, seats, message });
      setBooked(true);
      toast.success("Demande envoyée — le capitaine sera averti.");
      refetch();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    }
  };

  const handleFavorite = async () => {
    if (!user) { nav("/connexion"); return; }
    try {
      const { data } = await api.post(`/favorites/${tripId}`);
      setFavorited(data.favorited);
      toast.success(data.favorited ? "Ajouté à vos favoris" : "Retiré des favoris");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const share = (where) => {
    const links = {
      whatsapp: "https://chat.whatsapp.com/IGrJoS8cVpMDUnCSRX1Cty",
      facebook: "https://www.facebook.com/groups/1486535932408118",
    };
    window.open(links[where], "_blank", "noopener,noreferrer");
  };

  if (isLoading) {
    return <div className="max-w-5xl mx-auto px-6 py-24 text-center text-bretagne">Chargement…</div>;
  }
  if (!trip) {
    return <div className="max-w-5xl mx-auto px-6 py-24 text-center">Sortie introuvable.</div>;
  }

  return (
    <div className="bg-blanc">
      {/* Compact header — pas de grande photo */}
      <div className="bg-gradient-to-b from-brume/50 to-blanc border-b border-brume">
        <div className="max-w-5xl mx-auto px-6 pt-8 pb-6">
          <button
            data-testid="back-btn"
            onClick={() => nav(-1)}
            className="inline-flex items-center gap-1.5 text-sm text-bretagne hover:text-corail font-semibold"
          >
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
          <div className="mt-4 text-xs font-bold uppercase tracking-widest text-bretagne">
            {trip.type}
          </div>
          <div className="mt-1 flex items-start justify-between gap-3">
            <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-ocean leading-tight tracking-tight">
              {trip.title}
            </h1>
            <button
              data-testid="favorite-btn"
              onClick={handleFavorite}
              aria-label="Favori"
              className={`w-10 h-10 rounded-full grid place-items-center shrink-0 border ${
                favorited ? "bg-corail text-blanc border-corail" : "bg-blanc border-brume text-bretagne hover:border-corail hover:text-corail"
              }`}
            >
              <Heart className={`w-5 h-5 ${favorited ? "fill-blanc" : ""}`} />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10 grid lg:grid-cols-3 gap-8">
        {/* Main */}
        <div className="lg:col-span-2 space-y-8">
          {/* Status banner if non-active */}
          {trip.status && trip.status !== "active" && (
            <div data-testid="trip-status-banner" className={`rounded-2xl px-4 py-3 text-sm font-semibold ${
              trip.status === "cancelled" ? "bg-corail/15 text-corail border border-corail/30" :
              trip.status === "complete" ? "bg-ocean/10 text-ocean border border-ocean/20" :
              "bg-jaune/30 text-ocean border border-jaune/50"
            }`}>
              {trip.status === "cancelled" && "Cette sortie a été annulée par le capitaine."}
              {trip.status === "complete" && "Toutes les places sont prises — aucune nouvelle réservation possible."}
              {trip.status === "paused" && "Cette annonce est en pause — réservations temporairement suspendues."}
            </div>
          )}

          {/* Quick facts compact */}
          <div className="bg-blanc border border-brume rounded-2xl p-5">
            <ul className="grid sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
              <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-bretagne shrink-0" /><strong className="text-ocean">{trip.port}</strong></li>
              <li className="flex items-center gap-2"><Calendar className="w-4 h-4 text-bretagne shrink-0" /><span className="text-ocean">{fmtDateTime(trip.date)}</span></li>
              <li className="flex items-center gap-2"><Clock className="w-4 h-4 text-bretagne shrink-0" /><span className="text-ocean">{trip.duration_hours}h</span></li>
              <li className="flex items-center gap-2"><Users className="w-4 h-4 text-bretagne shrink-0" /><span className="text-ocean">{trip.spots_left}/{trip.spots_total} places · {trip.level}</span></li>
            </ul>
          </div>

          <section>
            <h2 className="font-display text-xl font-bold text-ocean mb-2">La sortie</h2>
            <p className="text-ocean/80 leading-relaxed whitespace-pre-line">{trip.description}</p>
          </section>

          {trip.gear_provided?.length > 0 && (
            <section>
              <h2 className="font-display text-xl font-bold text-ocean mb-3">Ce qui est fourni</h2>
              <ul className="grid sm:grid-cols-2 gap-2 text-sm">
                {trip.gear_provided.map((g) => (
                  <li key={g} className="inline-flex items-center gap-2 text-ocean/85">
                    <Check className="w-4 h-4 text-turquoise" /> {g}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {trip.cost_breakdown?.length > 0 && (
            <section>
              <h2 className="font-display text-xl font-bold text-ocean mb-3">Répartition des frais (estimation)</h2>
              <ul className="bg-sable border border-brume rounded-2xl divide-y divide-brume/70">
                {trip.cost_breakdown.map((c) => (
                  <li key={c.label} className="flex items-center justify-between px-4 py-2.5 text-sm">
                    <span className="text-ocean/80">{c.label}</span>
                    <span className="font-bold text-ocean">{c.amount} €</span>
                  </li>
                ))}
                <li className="flex items-center justify-between px-4 py-2.5 text-sm bg-brume/40">
                  <span className="text-bretagne font-bold uppercase tracking-widest text-xs">Total / personne</span>
                  <span className="font-display font-bold text-ocean">{trip.price_per_person} €</span>
                </li>
              </ul>
            </section>
          )}

          {/* Capitaine — simple */}
          <section>
            <h2 className="font-display text-xl font-bold text-ocean mb-3">Votre capitaine</h2>
            <Link
              to={`/capitaine/${trip.captain_user_id}`}
              data-testid="captain-profile-link"
              className="bg-blanc border border-brume rounded-2xl p-5 flex items-start gap-4 hover:border-corail/40 hover:shadow-matelot transition tactile"
            >
              <Avatar name={trip.captain_name} picture={trip.captain?.picture || trip.captain_picture} size={56} />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="font-display text-lg font-bold text-ocean hover:text-corail transition">{trip.captain_name}</span>
                  <FounderBadge show={trip.captain?.is_founder} variant="mini" />
                  {trip.captain_rating > 0 && (
                    <span className="inline-flex items-center gap-0.5 text-sm text-ocean">
                      <Star className="w-3.5 h-3.5 fill-jaune text-jaune" />{trip.captain_rating.toFixed(1)}
                    </span>
                  )}
                  <span className="text-xs text-bretagne">· {trip.captain?.trips_count || 0} sorties</span>
                </div>
                <div className="flex flex-wrap gap-2 mt-1.5">
                  {trip.captain?.verified_email && (
                    <span className="inline-flex items-center gap-1 bg-turquoise/15 text-turquoise text-xs font-bold px-2 py-0.5 rounded-full">
                      <Mail className="w-3 h-3" /> Email vérifié
                    </span>
                  )}
                  {trip.captain?.verified_captain && (
                    <span
                      className="inline-flex items-center gap-1 bg-corail/15 text-corail text-xs font-bold px-2 py-0.5 rounded-full"
                      title="Badge de confiance"
                    >
                      <Anchor className="w-3 h-3" /> Capitaine vérifié
                    </span>
                  )}
                </div>
                {trip.captain?.bio && (
                  <p className="text-sm text-ocean/75 mt-2 leading-relaxed line-clamp-3">{trip.captain.bio}</p>
                )}
                <div className="text-xs text-bretagne mt-2 font-semibold">Voir le profil →</div>
              </div>
            </Link>
          </section>

          {/* Share */}
          <section>
            <h2 className="font-display text-xl font-bold text-ocean mb-1">Rejoindre la communauté</h2>
            <p className="text-sm text-ocean/75 mb-3">
              Discutez avec d'autres pêcheurs bretons, partagez vos prises et restez au courant des prochaines sorties.
            </p>
            <div className="flex flex-wrap gap-2">
              <button data-testid="share-whatsapp" onClick={() => share("whatsapp")} className="inline-flex items-center gap-1.5 bg-[#25D366] text-blanc px-3.5 py-2 rounded-full font-semibold text-sm tactile">
                <MessageCircle className="w-4 h-4" /> Groupe WhatsApp
              </button>
              <button data-testid="share-facebook" onClick={() => share("facebook")} className="inline-flex items-center gap-1.5 bg-[#1877F2] text-blanc px-3.5 py-2 rounded-full font-semibold text-sm tactile">
                <Facebook className="w-4 h-4" /> Groupe Facebook
              </button>
            </div>
          </section>
        </div>

        {/* Sidebar booking */}
        <aside className="lg:sticky lg:top-24 self-start">
          <div className="bg-blanc rounded-2xl border-2 border-brume shadow-matelot p-5">
            <div className="text-[10px] uppercase tracking-widest text-bretagne font-bold">Participation aux frais</div>
            <div className="flex items-baseline gap-1 mt-0.5">
              <span className="font-display text-3xl font-bold text-ocean">{trip.price_per_person}€</span>
              <span className="text-sm text-bretagne">/ personne</span>
            </div>
            <p className="text-[11px] text-bretagne/80 mt-1 leading-snug">
              Montant indicatif. Carburant, appâts & consommables.
            </p>

            <div className="my-4 h-px bg-brume" />

            <div className="space-y-2 text-sm">
              <div className="flex justify-between gap-2">
                <span className="text-bretagne">Places restantes</span>
                <span className={`font-bold ${trip.spots_left <= 1 ? "text-corail" : "text-ocean"}`}>{trip.spots_left}</span>
              </div>
            </div>

            <div className="mt-4">
              <label className="text-[10px] uppercase tracking-widest text-bretagne font-bold">Nombre de places</label>
              <div className="mt-1.5 flex items-center gap-3">
                <button data-testid="seats-minus" onClick={() => setSeats(Math.max(1, seats - 1))} className="w-8 h-8 rounded-full bg-brume text-ocean font-bold">−</button>
                <span data-testid="seats-value" className="font-display text-xl text-ocean w-6 text-center">{seats}</span>
                <button data-testid="seats-plus" onClick={() => setSeats(Math.min(trip.spots_left || 1, seats + 1))} className="w-8 h-8 rounded-full bg-brume text-ocean font-bold">+</button>
              </div>
            </div>

            <Textarea
              data-testid="booking-message"
              placeholder="Un mot pour le capitaine (facultatif)"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="mt-4 bg-brume/30 border-brume rounded-xl text-sm"
              rows={3}
            />

            <Button
              data-testid="book-btn"
              onClick={handleBook}
              disabled={booked || trip.spots_left < 1 || (trip.status && trip.status !== "active")}
              className="w-full mt-4 bg-ocean hover:bg-ocean/90 text-blanc font-bold rounded-full py-5 tactile shadow-matelot disabled:opacity-60"
            >
              {booked ? "Demande envoyée"
                : trip.status === "cancelled" ? "Sortie annulée"
                : trip.status === "complete" ? "Complète"
                : trip.status === "paused" ? "En pause"
                : trip.spots_left < 1 ? "Complet"
                : "Demander ma place"}
            </Button>

            {/* Disclaimer paiement */}
            <div className="mt-4 bg-brume/40 border border-brume rounded-xl p-3 text-[11px] text-ocean/75 leading-relaxed flex gap-2">
              <Info className="w-3.5 h-3.5 text-bretagne shrink-0 mt-0.5" />
              <span>
                <strong className="text-ocean">Aucun paiement sur Le Bon Matelot.</strong>{" "}
                Le règlement de la participation ({trip.price_per_person * seats}€) se fait
                directement entre vous et le capitaine, à bord ou avant la sortie.
              </span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
